import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import Button from "@/v2-backoffice-client/components/button"
import TranslationEditorLanguage from "@/v2-backoffice-client/translation/editor/TranslationEditorLanguage"
import type { LangEdit } from "@/v2-backoffice-client/translation/editor/translation.editor.utils"
import type { SupportedLanguage } from "@/v2-backoffice-client/translation/translation.types"

export type TranslationAddRowLanguagesProps = {
  languages: SupportedLanguage[]
  edits: Record<string, LangEdit>
  onPatch(langCode: string, next: Partial<LangEdit>): void
  onSave(): void
  onAiFillMissing(): void
  onSuggestFrom(lang: SupportedLanguage): void
  filledLangCount: number
  missingLangCount: number
  canCreate: boolean
  // English (en-GB) is the anchor language every runtime read falls back
  // to; a Feature row saved without it fails the publish-time language
  // invariant. Gates "Save all changes" specifically — "AI translate
  // missing" is exempt since it drafts en-GB itself in the same call.
  hasEnglishName: boolean
  creatingRow: boolean
  aiFillLoading: boolean
  // Whether saving also drafts every language left empty. On by default: a
  // row saved with only en-GB lands straight in the Missing translations
  // queue, so the operator who just wrote it is the one who would have to
  // come back for it.
  autoTranslate: boolean
  onAutoTranslateChange(value: boolean): void
  onCancel(): void
}

// Pre-creation half of "Add text": every language editable at once (the row
// doesn't exist yet, so "Translate from" can't run — onSuggestFrom just
// explains that). Save-all and AI-fill both ultimately call the same
// ensureRowCreated in the parent, so they're kept mutually exclusive here
// (via `busy`) rather than letting one silently no-op while the other is
// mid-flight.
function TranslationAddRowLanguages(props: TranslationAddRowLanguagesProps) {
  const {
    languages,
    edits,
    onPatch,
    onSave,
    onAiFillMissing,
    onSuggestFrom,
    filledLangCount,
    missingLangCount,
    canCreate,
    hasEnglishName,
    creatingRow,
    aiFillLoading,
    autoTranslate,
    onAutoTranslateChange,
    onCancel,
  } = props

  const busy = creatingRow || aiFillLoading
  const willAutoTranslate = autoTranslate && missingLangCount > 0

  return (
    <>
      <div className="translation-editor-languages">
        {languages.map((lang) => (
          <TranslationEditorLanguage
            key={lang.code}
            lang={lang}
            edit={edits[lang.code]}
            isSource={false}
            hasGenerated={false}
            aiLoading={false}
            isSaving={creatingRow}
            disabled={busy || !hasEnglishName}
            onPatch={(next) => onPatch(lang.code, next)}
            onApplySuggestion={() => {}}
            onSave={onSave}
            onSuggestFrom={() => onSuggestFrom(lang)}
          />
        ))}
      </div>

      <div className="translation-editor-footer">
        <Button
          size="sm"
          data-feature-code="UI_BackofficeTranslation_AddRow_SaveAll"
          onClick={onSave}
          disabled={filledLangCount === 0 || busy || !hasEnglishName}
          isLoading={creatingRow}
          title={
            hasEnglishName
              ? willAutoTranslate
                ? "Create the text, write every language filled in above, then AI-draft the rest."
                : "Create the text and write every language filled in above, in one call."
              : 'English (en-GB) is required before saving — fill it in above, or use "AI translate missing" to draft it from the code.'
          }
        >
          {creatingRow
            ? "Saving…"
            : willAutoTranslate
              ? `Save and translate (${filledLangCount})`
              : `Save all changes (${filledLangCount})`}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          iconSrc="magic-wand"
          data-feature-code="UI_BackofficeTranslation_AddRow_AiFill"
          onClick={onAiFillMissing}
          disabled={!canCreate || busy}
          isLoading={aiFillLoading}
          title="Create the text (if needed) and AI-draft AND save every language still empty — from the code alone if nothing's filled in, or from whatever's already typed. Languages already filled in above are never touched."
        >
          {aiFillLoading
            ? "Translating…"
            : `AI translate missing (${missingLangCount})`}
        </Button>

        <Label className="ml-1 flex items-center gap-2 font-normal">
          <Checkbox
            checked={autoTranslate}
            disabled={busy}
            onCheckedChange={(isChecked) =>
              onAutoTranslateChange(isChecked === true)
            }
          />
          <span className="text-xs text-muted-foreground">
            Translate the rest on save
          </span>
        </Label>

        <div className="flex-1" />
        <Button
          variant="outline"
          data-feature-code="UI_BackofficeTranslation_AddRow_Cancel"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </>
  )
}

export default TranslationAddRowLanguages
